import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { BrandMark } from "../components/BrandMark";
import { PublicMobileMenu } from "../components/PublicMobileMenu";
import { toast } from "sonner";
import {
    Menu, X, ArrowRight, Calendar, CalendarDays, CalendarRange,
    PiggyBank, TrendingUp, Clock, Info, CheckCircle2, Percent,
    ArrowDownLeft, ArrowUpRight, Facebook, Instagram, Linkedin, MessageCircle,
} from "lucide-react";

/* ---------- nav ---------- */
const NAV = [
    { label: "Home", to: "/home" },
    { label: "About Us", to: "/about-us" },
    { label: "Loans", to: "/loans" },
    { label: "Saving Scheme", to: "/daily-savings" },
    { label: "Investment", to: "/investment" },
    { label: "Careers", to: "/careers" },
    { label: "Contact Us", to: "/contact-us" },
];

const FREQUENCIES = [
    { icon: Calendar, title: "Daily", desc: "Contribute a fixed amount every day toward your savings goal." },
    { icon: CalendarDays, title: "Weekly", desc: "A steady weekly contribution suited to weekly income cycles." },
    { icon: CalendarRange, title: "Monthly", desc: "A once-a-month contribution for planned monthly savings." },
];

const KEY_POINTS = [
    { icon: Clock, title: "Maximum tenure", desc: "365 days from the first approved deposit." },
    { icon: Percent, title: "Before 180 completed days", desc: "0% interest applied." },
    { icon: Percent, title: "180–364 completed days", desc: "3% p.a. applicable." },
    { icon: TrendingUp, title: "At 365 days", desc: "5% p.a. and account maturity." },
    { icon: Info, title: "Deposit-lot-wise calculation", desc: "Interest is calculated per deposit lot using its actual approved posting date." },
];

const FOOTER_LINKS = {
    Products: ["Personal Loan", "Business Loan", "Group Loan", "Saving Scheme", "Investment"],
    Company: ["About Us", "Careers", "News & Updates", "Contact Us"],
    Support: ["FAQs", "Grievance Redressal", "Customer Care", "Downloads"],
};

const placeholderLoginToast = () => {
    window.location.assign("/customer-login");
};

/* Indian number formatter (display only, no backend logic) */
const inr = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));

/* ---------- page ---------- */
export default function PublicDailySavings() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    /* visual-only calculator (illustrative, does not submit to backend) */
    const [amount, setAmount] = useState(100);
    const [frequency, setFrequency] = useState("Daily");
    const [days, setDays] = useState(200);

    const preview = useMemo(() => {
        const a = Math.max(0, Number(amount) || 0);
        const d = Math.max(0, Math.min(365, Number(days) || 0));
        const step = frequency === "Daily" ? 1 : frequency === "Weekly" ? 7 : 30;
        const N = Math.floor(d / step);          // number of deposit lots
        const D = d;                             // effective maturity day (0..365)
        // rate tier — 0% <180, 3% 180-364, 5% at 365
        const rate = d >= 365 ? 5 : d >= 180 ? 3 : 0;
        const deposited = a * N;
        // Lot-wise simple interest: lot i posted at day step*i earns
        // (D - step*i) days of interest.  Post-maturity lots earn 0.
        let sumDays = 0;
        for (let i = 1; i <= N; i++) {
            const lotDay = step * i;
            if (lotDay >= D) break;
            sumDays += (D - lotDay);
        }
        const interest = (a * rate / 100) * (sumDays / 365);
        return {
            deposited,
            rate,
            interest,
            total: deposited + interest,
            phase: d >= 365 ? "Maturity" : d >= 180 ? "180–364 completed days" : "Before 180 completed days",
        };
    }, [amount, frequency, days]);

    useEffect(() => {
        document.title = "Saving Scheme | Lokdhara Microfinance";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    const go = (to) => { if (to) navigate(to); };

    return (
        <div id="top" className="min-h-screen bg-white text-slate-900 antialiased">
            {/* HEADER ---------------------------------------------------- */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                    <Link to="/home" className="flex items-center" data-testid="ds-brand">
                        <BrandMark size={56} className="sm:!h-14" />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-700">
                        {NAV.map((n) => (
                            <button key={n.label}
                                onClick={() => go(n.to)}
                                className={`hover:text-brand-royal transition ${n.to === "/daily-savings" ? "text-brand-royal" : ""}`}
                                data-testid={`ds-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button
                            className="hidden sm:inline-flex bg-brand-royal hover:bg-brand-hover text-white rounded-full h-10 px-5"
                            onClick={placeholderLoginToast}
                            data-testid="ds-header-customer-login"
                        >
                            Customer Login
                        </Button>
                        <button
                            aria-label="Open menu"
                            className="lg:hidden h-10 w-10 rounded-lg border border-slate-200 grid place-items-center"
                            onClick={() => setMenuOpen(true)}
                            data-testid="ds-mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                {menuOpen && (
                    <PublicMobileMenu
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        nav={NAV}
                        onNavItemClick={(n) => go(n.to)}
                        onLoginClick={placeholderLoginToast}
                        testId="ds-mobile-menu"
                        activePath="/daily-savings"
                    />
                )}
            </header>

            {/* NAVY HERO ------------------------------------------------- */}
            <section className="relative overflow-hidden bg-brand-navy text-white">
                <div className="absolute inset-0 pointer-events-none opacity-20"
                    style={{ backgroundImage: "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 85% 65%, rgba(255,255,255,0.10) 0%, transparent 40%)" }}
                    aria-hidden
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-tint bg-white/10 rounded-full px-3 py-1">
                        Saving Scheme
                    </span>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight max-w-3xl">
                        Build Savings with Regular Contributions
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">
                        A structured savings option that lets you contribute at a frequency that suits
                        your income cycle — with clear rules for interest, tenure and withdrawals.
                    </p>
                </div>
            </section>

            {/* FREQUENCIES ---------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Contribution Frequencies
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Choose a rhythm that fits your income
                        </h2>
                    </div>
                    <div className="grid gap-6 sm:grid-cols-3">
                        {FREQUENCIES.map(({ icon: Icon, title, desc }) => (
                            <Card key={title}
                                className="rounded-2xl border-slate-100 p-7 hover:shadow-card-hover transition"
                                data-testid={`ds-freq-${title.toLowerCase()}`}
                            >
                                <div className="h-12 w-12 rounded-xl bg-brand-soft grid place-items-center">
                                    <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <h3 className="mt-5 font-display font-bold text-brand-navy text-xl">{title}</h3>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* KEY POINTS ----------------------------------------------- */}
            <section className="bg-slate-50 py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Product Rules
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Key points to know
                        </h2>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {KEY_POINTS.map(({ icon: Icon, title, desc }, i) => (
                            <Card key={title}
                                className="rounded-2xl border-slate-100 bg-white p-6"
                                data-testid={`ds-keypoint-${i + 1}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="h-11 w-11 rounded-xl bg-brand-tint grid place-items-center shrink-0">
                                        <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-900">{title}</div>
                                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</p>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* WITHDRAWAL ----------------------------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-start">
                    <div>
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Withdrawals
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Access your savings when you need
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Partial or full withdrawals are supported. Any applicable early-withdrawal
                            charge depends on how many days the deposit has stayed in the account.
                        </p>
                    </div>
                    <div className="grid gap-4">
                        <Card className="rounded-2xl border-slate-100 p-6"
                            data-testid="ds-withdraw-partial-full"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                                    <ArrowDownLeft className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900">Partial or full withdrawal</div>
                                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                                        Withdraw part of your savings or close the account fully at any
                                        time, subject to approved product rules.
                                    </p>
                                </div>
                            </div>
                        </Card>
                        <Card className="rounded-2xl border-slate-100 p-6"
                            data-testid="ds-withdraw-before-180"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-11 w-11 rounded-xl bg-amber-50 grid place-items-center shrink-0">
                                    <ArrowUpRight className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900">Before 180 days</div>
                                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                                        A 3% penalty applies on the principal withdrawn.
                                    </p>
                                </div>
                            </div>
                        </Card>
                        <Card className="rounded-2xl border-slate-100 p-6"
                            data-testid="ds-withdraw-180-364"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-11 w-11 rounded-xl bg-emerald-50 grid place-items-center shrink-0">
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900">180–364 days</div>
                                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                                        No early-withdrawal penalty applied.
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* ADVANCE COLLECTION --------------------------------------- */}
            <section className="bg-brand-soft py-20 sm:py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                        Advance Collection
                    </span>
                    <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                        Contribute ahead of schedule
                    </h2>
                    <p className="mt-5 text-slate-700 leading-relaxed max-w-2xl mx-auto">
                        If you contribute more than the current instalment, the advance amount is posted
                        to your upcoming scheduled instalment dates in order — keeping your account
                        record clear and predictable.
                    </p>
                </div>
            </section>

            {/* VISUAL CALCULATOR (placeholder, no backend) -------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Illustrative Preview
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Savings preview
                        </h2>
                        <p className="mt-3 text-sm text-slate-500 italic">
                            For illustration only — actual balances are calculated deposit-lot-wise on
                            approved posting dates.
                        </p>
                    </div>

                    <Card className="rounded-3xl border-slate-100 shadow-card p-6 sm:p-8 grid lg:grid-cols-2 gap-8"
                        data-testid="ds-calculator"
                    >
                        {/* inputs */}
                        <div className="space-y-5">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Contribution amount (₹)
                                </label>
                                <Input
                                    type="number"
                                    min={0}
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="mt-2 h-11 rounded-xl"
                                    data-testid="ds-calc-amount"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Frequency
                                </label>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                    {["Daily", "Weekly", "Monthly"].map((f) => (
                                        <button
                                            key={f}
                                            onClick={() => setFrequency(f)}
                                            className={`h-11 rounded-xl border text-sm font-semibold transition ${
                                                frequency === f
                                                    ? "bg-brand-navy text-white border-brand-navy"
                                                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                                            }`}
                                            data-testid={`ds-calc-freq-${f.toLowerCase()}`}
                                        >
                                            {f}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Days elapsed (0–365)
                                </label>
                                <Input
                                    type="number"
                                    min={0}
                                    max={365}
                                    value={days}
                                    onChange={(e) => setDays(e.target.value)}
                                    className="mt-2 h-11 rounded-xl"
                                    data-testid="ds-calc-days"
                                />
                                <div className="mt-2 text-xs text-slate-500">
                                    Current phase:{" "}
                                    <span className="font-semibold text-brand-navy">{preview.phase}</span>
                                </div>
                            </div>
                        </div>

                        {/* preview */}
                        <div className="rounded-2xl bg-brand-navy text-white p-6 sm:p-7 flex flex-col justify-between"
                            data-testid="ds-calc-preview"
                        >
                            <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-xl bg-white/10 grid place-items-center">
                                    <PiggyBank className="h-5 w-5 text-brand-tint" strokeWidth={1.75} />
                                </div>
                                <div className="text-xs font-semibold uppercase tracking-widest text-brand-tint">
                                    Illustrative Result
                                </div>
                            </div>

                            <div className="mt-6 space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white/70">Deposits (approx.)</span>
                                    <span className="font-display font-bold text-lg">₹ {inr(preview.deposited)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white/70">Applicable rate</span>
                                    <span className="font-display font-bold text-lg">{preview.rate}% p.a.</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white/70">Interest (illustrative)</span>
                                    <span className="font-display font-bold text-lg">₹ {inr(preview.interest)}</span>
                                </div>
                                <div className="border-t border-white/10 pt-4 flex items-center justify-between">
                                    <span className="text-sm text-white/70">Estimated balance</span>
                                    <span className="font-display font-bold text-2xl text-brand-tint">
                                        ₹ {inr(preview.total)}
                                    </span>
                                </div>
                            </div>

                            <p className="mt-6 text-[11px] leading-relaxed text-white/60">
                                Actual calculations are performed lot-wise on approved posting dates,
                                subject to product rules.
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            {/* REQUIRED NOTE -------------------------------------------- */}
            <section className="pb-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Card className="rounded-2xl border-brand-soft bg-brand-soft/50 p-6 sm:p-7 flex items-start gap-4"
                        data-testid="ds-important-note"
                    >
                        <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shrink-0">
                            <Info className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-royal">
                                Important Note
                            </div>
                            <p className="mt-2 text-slate-700 leading-relaxed">
                                Rates, calculations, withdrawals and account terms are subject to
                                approved product rules.
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            {/* CTA ------------------------------------------------------ */}
            <section className="bg-gradient-to-br from-brand-navy to-brand-royal text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Start building your savings, one contribution at a time.
                    </h2>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button
                            className="bg-white text-brand-navy hover:bg-brand-soft rounded-full h-12 px-6 font-semibold"
                            onClick={() => navigate("/home")}
                            data-testid="ds-cta-contact-btn"
                        >
                            Contact Us <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                            onClick={placeholderLoginToast}
                            data-testid="ds-cta-customer-login"
                        >
                            Customer Login
                        </Button>
                    </div>
                </div>
            </section>

            {/* FOOTER --------------------------------------------------- */}
            <footer className="bg-brand-navy text-slate-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
                    <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <BrandMark size={40} variant="white" />
                            <p className="mt-5 text-sm leading-relaxed max-w-md text-slate-400">
                                Lokdhara Microfinance is focused on providing structured and accessible
                                financial services through transparent processes, clear records and
                                customer-oriented support.
                            </p>
                            <div className="mt-5 flex gap-3">
                                {[Facebook, Instagram, Linkedin, MessageCircle].map((Icon, i) => (
                                    <a key={i} href="/home"
                                        className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition"
                                        aria-label={`social-${i}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {Object.entries(FOOTER_LINKS).map(([col, items]) => (
                            <div key={col}>
                                <div className="text-white font-semibold text-sm mb-4">{col}</div>
                                <ul className="space-y-2 text-sm">
                                    {items.map((item) => (
                                        <li key={item}>
                                            <button
                                                onClick={() => {
                                                    if (item === "About Us") navigate("/about-us");
                                                    else if (["Personal Loan", "Business Loan", "Group Loan"].includes(item)) navigate("/loans");
                                                    else if (item === "Saving Scheme") navigate("/daily-savings");
                                                    else navigate("/home");
                                                }}
                                                className="hover:text-white transition text-left"
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-xs text-slate-400 leading-relaxed max-w-3xl">
                        Product availability, eligibility, rates and terms are subject to approval and
                        applicable requirements.
                    </p>

                    <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                        <div>© {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.</div>
                        <div className="flex items-center gap-5">
                            <button onClick={() => navigate("/home")} className="hover:text-white">Privacy Policy</button>
                            <button onClick={() => navigate("/home")} className="hover:text-white">Terms &amp; Conditions</button>
                            <Link
                                to="/login"
                                className="text-slate-300 hover:text-white underline underline-offset-4"
                                data-testid="ds-footer-staff-login"
                            >
                                Staff Login
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
